import moment from 'moment-timezone';
import ld from 'lodash';
import store from '../store';
import CommonUtils from './common-utils';

export default class DateTimeUtils {
  public static formatToSave(s: string, userInfo: any) {
    try {
      if (CommonUtils.hasText(s)) {
        userInfo = userInfo || store.getters['settings/getUserInfo'] || { displaySettings: { dateStyling: { dateFormat: 1, dateDelimiter: '/' } } };
        const displaySettings = userInfo.displaySettings;
        const dateFormat = +displaySettings.dateStyling.dateFormat;
        const dateDelimiter = displaySettings.dateStyling.dateDelimiter;
        let showFormat = 'MM/DD/YYYY';
        if (dateFormat === 2) {
          showFormat = 'DD/MM/YYYY';
        }
        showFormat = showFormat.replace(/\//g, dateDelimiter);
        const mToSaveDate = moment(s, showFormat);
        return mToSaveDate.format('MM/DD/YYYY');
      } else {
        return '';
      }
    } catch (e) {
      return '';
    }
  }

  public static formatToShow(s: string, userInfo: any) {
    try {
      if (CommonUtils.hasText(s)) {
        userInfo = userInfo || store.getters['settings/getUserInfo'] || { displaySettings: { dateStyling: { dateFormat: 1, dateDelimiter: '/' } } };
        const displaySettings = userInfo.displaySettings;
        const dateFormat = +displaySettings.dateStyling.dateFormat;
        const dateDelimiter = displaySettings.dateStyling.dateDelimiter;
        let showFormat = 'MM/DD/YYYY';
        if (dateFormat === 2) {
          showFormat = 'DD/MM/YYYY';
        }
        showFormat = showFormat.replace(/\//g, dateDelimiter);
        const mToShowDate = moment(s, 'MM/DD/YYYY');
        return mToShowDate.format(showFormat);
      }
      return '';
    } catch (e) {
      return '';
    }
  }

  public static addDays(d: Date, days: number) {
    d.setDate(d.getDate() + days);
    return d;
  }

  public static format(d: Date) {
    try {
      return DateTimeUtils.padLeftZero((d.getMonth() + 1), 2) + '/' + DateTimeUtils.padLeftZero(d.getDate(), 2) + '/' + d.getFullYear();
    } catch (e) {
    }
    return '';
  }

  public static toDate(s: string): Date {
    if (!s || s.length < 10) {
      return undefined as any;
    }
    if (s.includes('/')) {
      const values = s.split('/');
      if (values.length === 3) {
        return new Date(values[2] + '-' + values[0] + '-' + values[1] + 'T00:00:00');
      }
    } else if (s.includes('-')) {
      return new Date(s + 'T00:00:00');
    }
    return undefined as any;
  }

  static padLeftZero(str: any, len: number): string {
    str = str.toString();
    return str.length < len ? DateTimeUtils.padLeftZero('0' + str, len) : str;
  }

  public static getMonday(date: Date): Date {
    const monday = new Date();
    monday.setTime(date.getTime())
    const day = monday.getDay();
    switch (day) {
      case 0: monday.setDate(monday.getDate() + 1); break;
      case 1: monday.setDate(monday.getDate() + 0); break;
      case 2: monday.setDate(monday.getDate() - 1); break;
      case 3: monday.setDate(monday.getDate() - 2); break;
      case 4: monday.setDate(monday.getDate() - 3); break;
      case 5: monday.setDate(monday.getDate() - 4); break;
      default: monday.setDate(monday.getDate() + 2);
    }
    return monday;
  }

  public static isTimeFormatValid(time: string) {
    return time === '' || moment(time, 'h:mmA', true).isValid() || moment(time, 'h:mm A', true).isValid();
  }

  public static isDateFormatValid(date: string) {
    return date === '' || moment(date, 'MM/DD/YYYY', true).isValid() || moment(date, 'YYYY-MM-DD', true).isValid();
  }

  public static toTime(text: string) {
    let m = moment(text, 'h:mmA', true);
    if (m.isValid()) {
      const date = m.toDate();
      return {
        minute: date.getMinutes(),
        hour: date.getHours()
      };
    } else {
      m = moment(text, 'h:mm A', true);
      if (m.isValid()) {
        const date = m.toDate();
        return {
          minute: date.getMinutes(),
          hour: date.getHours()
        };
      }
    }
    const date = new Date();
    return {
      minute: date.getMinutes(),
      hour: date.getHours()
    };
  }

  public static areTimesEqual(text1: string, text2: string) {
    const time1 = DateTimeUtils.toTime(text1);
    const time2 = DateTimeUtils.toTime(text2);
    return ld.isEqual(time1, time2);
  }

  public static daysDiff(dStart: string, dEnd: string) {
    const mStart = moment(dStart, 'MM/DD/YYYY');
    const mEnd = moment(dEnd, 'MM/DD/YYYY');
    return moment.duration(mEnd.diff(mStart)).asDays();
  }

  public static isInWeeklySchedule(date: string, dStart: string, dEnd: string) {
    const mStart = moment(dStart, 'MM/DD/YYYY');
    const mEnd = moment(dEnd, 'MM/DD/YYYY');
    const mDate = moment(date, 'MM/DD/YYYY');
    return mDate.isBetween(mStart, mEnd, undefined, '[]') && mDate.weekday() === mStart.weekday();
  }

  public static isInBiweeklySchedule(date: string, dStart: string, dEnd: string) {
    const mStart = moment(dStart, 'MM/DD/YYYY');
    const mEnd = moment(dEnd, 'MM/DD/YYYY');
    const mDate = moment(date, 'MM/DD/YYYY');
    return mDate.isBetween(mStart, mEnd, undefined, '[]') && CommonUtils.isEven(mDate.diff(mStart, 'week')) && mDate.weekday() === mStart.weekday();
  }

  public static isBetween(date: string, dStart: string, dEnd: string) {
    return moment(date, 'MM/DD/YYYY').isBetween(moment(dStart, 'MM/DD/YYYY'), moment(dEnd, 'MM/DD/YYYY'), undefined, '[]');
  }

  public static formatDate(date: Date | string, pattern: string) {
    return moment(date, 'MM/DD/YYYY').tz(moment.tz.guess()).format(pattern);
  }

  public static getCurrentYearRange() {
    const currentYear = new Date().getFullYear();
    return {
      start: currentYear,
      end: currentYear + 1,
      range: (currentYear + '-' + (currentYear + 1))
    };
  }

  public static currentDate() {
    return DateTimeUtils.formatDate(new Date(), 'MM/DD/YYYY');
  }

  public static currentDateAddDays(days: number) {
    return DateTimeUtils.formatDate(moment().add(days, 'days').toDate(), 'MM/DD/YYYY');
  }

  public static currentDateAddMonths(months: number) {
    return DateTimeUtils.formatDate(moment().add(months, 'months').toDate(), 'MM/DD/YYYY');
  }

  public static getTawDay(date: moment.Moment) {
    const tawWeek = moment().month(4).startOf('month').add(6 - moment().day(1).day(), 'days').week();

    if (date.week() === tawWeek && date.day() !== 0 && date.day() !== 6) {
      return date.day() - 1;
    }

    return -1;
  }
}
